import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ShieldAlert } from 'lucide-react';
import { EmptyState, SkeletonList } from '@/ui';
import { useSessionStore } from './session.store';
import { hasAnyPermission, isOrgAdmin } from './types';
import { loginPathWithNext } from './next-path';
import { zoneHome, zoneOf, type Zone } from './zone-home';

/**
 * KORUMA ZINCIRI — disardan ice, her biri AYRI bir bilesen.
 *
 *   1. RequireAuth          oturum yok        -> /giris?next=...
 *   2. RequireFreshPassword sifre degismeli   -> /ilk-giris
 *   3. RequireConsent       onay eksik        -> /sozlesmeler
 *   4. RequireZone          yanlis bolge      -> kendi bolgesinin acilisi
 *   5. RequirePermission    yetki yok         -> 403 PANELI (yonlendirme YOK)
 *
 * 2 ve 3, BOLGE YONLENDIRMESINDEN ONCE calisir: sifresini degistirmemis ya da
 * sozlesmeyi onaylamamis bir kullanicinin hangi bolgeye ait oldugu sorusunun
 * pratik bir karsiligi yok; once kapinin acilmasi gerekiyor.
 *
 * BES FARKLI BASARISIZLIK, BES FARKLI DAVRANIS — ve bu bilincli:
 *   - "kim oldugunu bilmiyorum"        -> giris ekrani, doneceği yer saklanir
 *   - "once sunu yapmalisin"           -> zorunlu adima yonlendirme
 *   - "burasi senin bolgen degil"      -> kendi bolgene yonlendirme
 *   - "bu ekran senin ama bu bolum degil" -> SAYFA ACILIR, icerik yerine ret
 *     paneli gorunur. Yonlendirmek, kullaniciya nereye tiklarsa tiklasin
 *     baska bir yere firlatildigini gosterir ve sebebini hic soylemez.
 *
 * Bunlarin hicbiri GUVENLIK SINIRI DEGIL. Sinir sunucuda; buradaki hata en
 * fazla yanlis menu gosterir, veriyi acmaz.
 */

/** Ilk acilis: ozet okundu, sessiz yenileme bekleniyor. */
function SessionBooting() {
  const { t } = useTranslation();
  return (
    <div aria-busy="true" aria-live="polite">
      <span className="visually-hidden">{t('common.loading')}</span>
      <SkeletonList rows={3} />
    </div>
  );
}

/** 403: rota acilir, icerik yerine bu goruntulenir. */
export function PermissionDenied() {
  const { t } = useTranslation();
  return (
    <EmptyState
      tone="error"
      icon={<ShieldAlert size={28} aria-hidden="true" />}
      title={t('errors.noPermission')}
      description={t('errors.noPermissionDetail')}
    />
  );
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const status = useSessionStore((state) => state.status);
  const snapshot = useSessionStore((state) => state.snapshot);
  const location = useLocation();

  if (status === 'hydrating') return <SessionBooting />;
  if (status !== 'authenticated' || !snapshot) {
    // location.pathname taban yolu ICERMEZ (react-router basename'i soyar), bu
    // yuzden dogrudan `next` olarak yazilabilir.
    return <Navigate to={loginPathWithNext(location.pathname, location.search)} replace />;
  }
  return <>{children}</>;
}

export function RequireFreshPassword({ children }: { children: ReactNode }) {
  const snapshot = useSessionStore((state) => state.snapshot);
  if (snapshot?.mustChangePassword) return <Navigate to="/ilk-giris" replace />;
  return <>{children}</>;
}

export function RequireConsent({ children }: { children: ReactNode }) {
  const snapshot = useSessionStore((state) => state.snapshot);
  const consents = snapshot?.consents;
  // Sirali: once aydinlatma, sonra kullanim kosullari. Ikisi de eksikse ayni
  // rotaya gider; sihirbaz hangi adimda oldugunu kendisi bilir.
  if (consents && (!consents.kvkkAccepted || !consents.usageAgreementAccepted)) {
    return <Navigate to="/sozlesmeler" replace />;
  }
  return <>{children}</>;
}

export function RequireZone({ zone, children }: { zone: Zone; children: ReactNode }) {
  const snapshot = useSessionStore((state) => state.snapshot);
  if (!snapshot) return null;
  if (zoneOf(snapshot) !== zone) return <Navigate to={zoneHome(snapshot)} replace />;
  return <>{children}</>;
}

export function RequirePermission({
  code,
  children,
}: {
  /** Tek kod ya da "bunlardan biri" listesi. */
  code: string | readonly string[];
  children: ReactNode;
}) {
  const snapshot = useSessionStore((state) => state.snapshot);
  // Org yoneticisi ORTUK olarak tam yetkilidir ve grant listesine karsi
  // SINANMAZ — sunucu yonetici icin grant satiri yazmaz (bkz. types.ts).
  const codes = typeof code === 'string' ? [code] : code;
  if (!hasAnyPermission(snapshot, codes)) return <PermissionDenied />;
  return <>{children}</>;
}

/**
 * Yalniz bir organizasyonun UYESINE acik ekranlar (organizasyon raporlari). Org'suz kullanici
 * grant sisteminin disinda oldugu icin yetki kontrolunden gecer (hasPermission), ama bu uclar
 * sunucuda organizasyon baglami ister (403 NO_ORGANIZATION_CONTEXT).
 */
export function RequireOrganization({ children }: { children: ReactNode }) {
  const snapshot = useSessionStore((state) => state.snapshot);
  if (!snapshot?.organization) return <PermissionDenied />;
  return <>{children}</>;
}

/**
 * `adminOnly` rotalarin karsiligi — RequirePermission'in ikizi.
 *
 * Yonetici yetkileri (manageMembers, managePermissions, editOrganization,
 * viewAuditLog) icin GRANT KODU YOK: sunucu bunlari yaziya dokmez, yonetici
 * ortuk olarak tam yetkilidir. Bu yuzden bir kod ile sinanamazlar ve ayri bir
 * bilesen gerekiyor. Davranis aynidir: yonlendirme degil, RET PANELI.
 */
export function RequireOrgAdmin({ children }: { children: ReactNode }) {
  const snapshot = useSessionStore((state) => state.snapshot);
  if (!isOrgAdmin(snapshot)) return <PermissionDenied />;
  return <>{children}</>;
}
